// @ts-strict-ignore
import {Component, Input, IterableDiffers, OnChanges, SimpleChanges} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {
	SimAudioChannelService,
	SimConfigurationService,
	SimDebriefingService,
	SimEnumExtendedService,
	NotificationService,
	SimSystemExtendedService,
	SimUtilityService,
} from "@services";
import Enumerable from "linq";
import {
	ArchiveChannelStateResponse,
	AudioChannelGroup,
	AudioChannelState,
	AudioDeviceIconType,
	AudioDeviceInputChannelType,
	AudioInputChannel,
	ScenarioRecording,
} from "@models";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";

@Component({
	selector: "sim-audio-channel-control-panel",
	templateUrl: "./audio-channel-control-panel.component.html",
	styleUrls: ["./audio-channel-control-panel.component.scss"],
	providers: [EventEmitterService],
	standalone: false,
})
export class AudioChannelControlPanelComponent implements OnChanges {
	T = Translations;
	AudioDeviceIconType = AudioDeviceIconType;
	setting = SimConfigurationService.settings;

	@Input() scenarioRecording: ScenarioRecording = null;
	@Input() scenarioRecordingSceneIndex = 0;

	@Input() audioChannelGroupStates: {[key: string]: AudioChannelState};
	@Input() audioChannelStates: {[key: string]: AudioChannelState};
	@Input() showSolo = true;
	@Input() showMute = true;

	public audioChannelGroups: AudioChannelGroup[] = null;
	public audioChannels: AudioInputChannel[] = null;

	constructor(
		protected _emitterService: EventEmitterService,
		protected _simSystemService: SimSystemExtendedService,
		protected _simAudioChannelService: SimAudioChannelService,
		protected _simEnumExtendedService: SimEnumExtendedService,
		protected _simDebriefingService: SimDebriefingService,
		protected _simNotificationService: NotificationService,
		protected translate: TranslateService,
		protected differs: IterableDiffers,
	) {}

	public async ngOnChanges(changes: SimpleChanges) {
		if (
			changes["scenarioRecordingSceneIndex"] ||
			(changes["scenarioRecording"] && (changes["scenarioRecording"]?.previousValue as ScenarioRecording))?.id !==
				this.scenarioRecording?.id
		) {
			this.audioChannels = this.getCurrentSceneAudioChannels();
			await this.groupAudioChannels();
			this.addAudioChannelAttributes();
		}
	}

	private getCurrentSceneAudioChannels() {
		let scene = this.scenarioRecording?.recordingScenes?.[this.scenarioRecordingSceneIndex];
		return (scene?.audioFiles?.[0]?.audioInputChannels ?? []).filter((x) => x.id !== SimConfigurationService.settings.silentChannelId);
	}

	private getCurrentSceneAudioGroupChannels() {
		let scene = this.scenarioRecording?.recordingScenes?.[this.scenarioRecordingSceneIndex];
		return scene?.audioFiles?.[0]?.audioChannelGroups ?? [];
	}

	private async groupAudioChannels() {
		this.audioChannelGroups = this.getCurrentSceneAudioGroupChannels();
		if (!this.audioChannelGroups.find((x) => x.id === SimUtilityService.emptyGuid())) {
			this.audioChannelGroups.unshift(await this._simAudioChannelService.getUnassignedAudioChannelGroup());
		}

		for (const audioChannelGroup of this.audioChannelGroups) {
			audioChannelGroup["channels"] = Enumerable.from(this.audioChannels)
				.where(
					(audioChannel) =>
						audioChannel.audioChannelGroupId === audioChannelGroup.id ||
						(!audioChannel.audioChannelGroupId && audioChannelGroup.id === SimUtilityService.emptyGuid()),
				)
				.toArray();
		}
	}

	private addAudioChannelAttributes() {
		for (const guiChannel of this.audioChannels || []) {
			if (
				guiChannel.type === AudioDeviceInputChannelType.VOG ||
				guiChannel.type === AudioDeviceInputChannelType.VTP ||
				guiChannel.type === AudioDeviceInputChannelType.VttInputChannel
			) {
				guiChannel["title"] = this.translate.instant(this.T.RECORDER_TXT_HOVER_MUTEDPC_ROOM);
				guiChannel["specialIconClass"] = "sstAC_specialIconClass_room";
			} else {
				guiChannel["title"] = this.translate.instant(this.T.RECORDER_TXT_HOVER_MUTEPC);
			}
		}
	}

	public getFontIcon(audioDeviceIconType: AudioDeviceIconType) {
		return this._simEnumExtendedService.getFontIcon(audioDeviceIconType);
	}

	public async initializeAudioChannelGroups() {
		this.audioChannelGroups = await this._simAudioChannelService.getAudioChannelGroups();
	}

	public toggleSolo(channelId: string, channelGroupId: string) {
		if (channelId) {
			this._simDebriefingService
				.toggleSoloArchive({
					audioChannelId: channelId,
					audioChannelGroupId: channelGroupId,
					allAudioInputChannels: this.audioChannels,
					archiveAudioStates: {
						audioChannelStates: this.audioChannelStates,
						audioChannelGroupStates: this.audioChannelGroupStates,
					},
				})
				.then((response) => {
					this.setResponse(response);
				});
		}
	}

	public toggleMute(channelId: string, channelGroupId?: string) {
		if (channelId) {
			this._simDebriefingService
				.toggleMuteArchive({
					audioChannelId: channelId,
					allAudioInputChannels: this.audioChannels,
					audioChannelGroupId: channelGroupId,
					archiveAudioStates: {
						audioChannelStates: this.audioChannelStates,
						audioChannelGroupStates: this.audioChannelGroupStates,
					},
				})
				.then((response) => {
					this.setResponse(response);
				});
		}
	}

	public setVolume(channelId: string, channelGroupId: string, volume: number) {
		if (channelId) {
			this._simDebriefingService
				.setVolumeArchive({
					audioChannelId: channelId,
					audioChannelGroupId: channelGroupId,
					volume: volume,
					allAudioInputChannels: this.audioChannels,
					archiveAudioStates: {
						audioChannelStates: this.audioChannelStates,
						audioChannelGroupStates: this.audioChannelGroupStates,
					},
				})
				.then((response) => {
					this.setResponse(response);
				});
		}
	}

	public toggleGroupSolo(channelGroupId: string) {
		if (channelGroupId) {
			this._simDebriefingService
				.toggleGroupSoloArchive({
					audioChannelGroupId: channelGroupId,
					allAudioInputChannels: this.audioChannels,
					archiveAudioStates: {
						audioChannelStates: this.audioChannelStates,
						audioChannelGroupStates: this.audioChannelGroupStates,
					},
				})
				.then((response) => {
					this.setResponse(response);
				});
		}
	}

	public toggleGroupMute(channelGroupId: string) {
		if (channelGroupId) {
			this._simDebriefingService
				.toggleGroupMuteArchive({
					audioChannelGroupId: channelGroupId,
					allAudioInputChannels: this.audioChannels,
					archiveAudioStates: {
						audioChannelStates: this.audioChannelStates,
						audioChannelGroupStates: this.audioChannelGroupStates,
					},
				})
				.then((response) => {
					this.setResponse(response);
				});
		}
	}

	public setGroupVolume(channelGroupId: string, volume: number) {
		if (channelGroupId) {
			this._simDebriefingService
				.setGroupVolumeArchive({
					audioChannelGroupId: channelGroupId,
					volume: volume,
					allAudioInputChannels: this.audioChannels,
					archiveAudioStates: {
						audioChannelStates: this.audioChannelStates,
						audioChannelGroupStates: this.audioChannelGroupStates,
					},
				})
				.then((response) => {
					this.setResponse(response);
				});
		}
	}

	private setResponse(state: ArchiveChannelStateResponse) {
		this.audioChannelStates = SimUtilityService.copy(state.audioChannelStates, this.audioChannelStates);
		this.audioChannelGroupStates = SimUtilityService.copy(state.audioChannelGroupStates, this.audioChannelGroupStates);
	}
}
